import React, { useState } from "react";
import { Sparkles, Image as ImageIcon, Wand2, RefreshCw, Palette } from "lucide-react";
import { generateLayout, updatePage, analyzePhotos, generateCover } from "../api";
import { toast } from "sonner";
import { useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";

// Font Mapper
const fontMap = {
    "Handwriting": "'Dancing Script', cursive",
    "Serif": "'Playfair Display', serif",
    "Modern": "'Lato', sans-serif",
    "Display": "'Amatic SC', cursive"
};

// Draggable Photo Component
const DraggablePhoto = ({ photo }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "photo",
    item: { id: photo.id, url: photo.url },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      className={`aspect-square bg-slate-100 rounded-lg overflow-hidden cursor-move border-2 relative group ${
        isDragging ? "border-indigo-500 opacity-50" : "border-transparent"
      }`}
    >
      <img
        src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
        alt="thumbnail"
        className="w-full h-full object-cover"
      />
      {photo.analysis && (
          <div className="absolute inset-0 bg-black/60 text-white text-[10px] p-1 opacity-0 group-hover:opacity-100 overflow-y-auto">
              {photo.analysis}
          </div>
      )}
    </div>
  );
};

// Drop Zone Component
const PhotoSlot = ({ assignedPhoto, onDrop, onRemove, design }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: "photo",
    drop: (item) => onDrop(item),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));
  
  // Dynamic styles from AI
  const slotStyle = {
      borderRadius: design?.border_radius || "8px",
      transform: design?.layout_style === 'scrapbook' ? `rotate(${Math.random() * 6 - 3}deg)` : 'none',
      border: design?.layout_style === 'polaroid' ? '12px solid white' : 'none',
      boxShadow: design?.layout_style === 'polaroid' ? '0 4px 6px rgba(0,0,0,0.1)' : 'none'
  };

  return (
    <div
      ref={drop}
      style={slotStyle}
      className={`aspect-[4/3] flex items-center justify-center transition-colors relative group overflow-hidden ${
        isOver
          ? "border-2 border-indigo-500 bg-indigo-50"
          : assignedPhoto
          ? ""
          : "border-2 border-dashed border-slate-300 bg-white/50"
      }`}
    >
      {assignedPhoto ? (
        <>
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${assignedPhoto.url}`}
            alt="slot"
            className="w-full h-full object-cover"
          />
          <button
            onClick={onRemove}
            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
            title="Remove photo"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </>
      ) : (
        <div className="text-slate-400 flex flex-col items-center">
          <ImageIcon className="w-6 h-6 mb-1" />
          <span className="text-xs">Drop Photo</span>
        </div>
      )}
    </div>
  );
};

const MagicDesigner = ({ projectId, project, onUpdate }) => {
  const [form, setForm] = useState({
    topic: "",
    description: "",
    page_count: 5,
    format: "A4 Landscape",
    style_prompt: "A creative photo book page layout designed by AI. Artistic moodboard style. A collage of 5 photos from a summer trip, varying sizes and aspect ratios. The photos slightly overlap in an aesthetically pleasing way. Soft pastel color palette derived from the photos. Minimal sans-serif text indicating date and location in the corner. High-quality paper texture background, professional graphic design, photography portfolio style"
  });
  
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingCover, setGeneratingCover] = useState(false);
  const [layout, setLayout] = useState(project.layout || null);

  const availablePhotos = (project.photos || []).filter((p) => p.status === "accepted");

  const handleAnalyze = async () => {
      setAnalyzing(true);
      try {
          toast.info("Analyzing your photos... This might take a moment.");
          await analyzePhotos(projectId);
          toast.success("Photos analyzed!");
          onUpdate();
      } catch (err) {
          toast.error("Analysis failed");
      } finally {
          setAnalyzing(false);
      }
  };

  const handleGenerate = async () => {
    if (!form.topic || !form.description) return toast.error("Please fill in Topic and Description");
    setGenerating(true);
    try {
      const data = await generateLayout(projectId, form);
      setLayout(data);
      toast.success("Magic layout created!");
      onUpdate(); 
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate layout");
    } finally {
      setGenerating(false);
    }
  };
  
  const handleGenerateCover = async () => {
      if (!form.topic) return toast.error("Please enter a topic first");
      setGeneratingCover(true);
      try {
          toast.info("Creating cover art...");
          await generateCover(projectId, { topic: form.topic, style_prompt: form.style_prompt });
          toast.success("Cover generated!");
          onUpdate();
      } catch (err) {
          toast.error("Failed to generate cover");
      } finally {
          setGeneratingCover(false);
      }
  }
  
  const handleDropPhoto = async (pageIndex, slotIndex, photoItem) => {
    const newLayout = [...layout];
    const page = newLayout[pageIndex];
    if (!page.assigned_photo_ids) page.assigned_photo_ids = [];
    while (page.assigned_photo_ids.length < page.photo_slots) {
        page.assigned_photo_ids.push(null);
    }
    page.assigned_photo_ids[slotIndex] = photoItem.id;
    setLayout(newLayout);
    try {
        const cleanIds = page.assigned_photo_ids.filter(id => id !== null);
        await updatePage(projectId, page.page_number, cleanIds);
    } catch(e) {
        toast.error("Failed to save assignment");
    }
  };
  
  const handleRemovePhoto = async (pageIndex, slotIndex) => {
    const newLayout = [...layout];
    const page = newLayout[pageIndex];
    page.assigned_photo_ids[slotIndex] = null;
    setLayout(newLayout);
    try {
        const cleanIds = page.assigned_photo_ids.filter(id => id !== null);
        await updatePage(projectId, page.page_number, cleanIds);
    } catch(e) {
         toast.error("Failed to update");
    }
  }

  const getPhotoById = (id) => availablePhotos.find((p) => p.id === id);

  if (!layout && !generating) {
    return (
      <div className="max-w-6xl mx-auto py-12 px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-xl mb-8">
                    <div className="flex items-center gap-3 mb-6">
                        <Sparkles className="w-8 h-8 text-indigo-600" />
                        <h2 className="text-2xl font-bold text-slate-900">AI Art Director Setup</h2>
                    </div>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Topic / Title</label>
                            <input 
                                className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                placeholder="e.g. Vintage Roadtrip 1999"
                                value={form.topic}
                                onChange={(e) => setForm({...form, topic: e.target.value})}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Pages</label>
                                <input 
                                    type="number"
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                    value={form.page_count}
                                    onChange={(e) => setForm({...form, page_count: parseInt(e.target.value)})}
                                    min={1}
                                    max={50}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Format</label>
                                <select 
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                                    value={form.format}
                                    onChange={(e) => setForm({...form, format: e.target.value})}
                                >
                                    <option>A4 Landscape</option>
                                    <option>A4 Portrait</option>
                                    <option>Square (20x20cm)</option>
                                    <option>Large Square (30x30cm)</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Story & Mood Description</label>
                            <textarea
                                className="w-full h-32 border border-slate-300 rounded-lg px-3 py-2 resize-none"
                                placeholder="Describe the flow and the FEELING. e.g. 'Chaotic but fun family gathering, lots of laughter, bright sunny colors' or 'Melancholic rainy days in London, black and white vibe'."
                                value={form.description}
                                onChange={(e) => setForm({...form, description: e.target.value})}
                            />
                        </div>
                    </div>
                    
                    <button
                        onClick={handleGenerate}
                        disabled={generating}
                        className="w-full mt-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all shadow-lg shadow-indigo-200 transform hover:scale-[1.02]"
                    >
                        {generating ? "Designing..." : <><Sparkles className="w-5 h-5" /> Generate Bespoke Design</>}
                    </button>
                </div>
            </div>
            
            <div className="lg:col-span-1 space-y-4">
                 <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-medium text-slate-900">Photo Analysis</h3>
                            <p className="text-xs text-slate-500">Required for smart selection</p>
                        </div>
                    </div>
                    <button 
                        onClick={handleAnalyze}
                        disabled={analyzing}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
                    >
                        {analyzing ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Wand2 className="w-4 h-4 text-purple-600"/>}
                        {analyzing ? "Analyzing..." : "Analyze Photos with AI"}
                    </button>
                </div>

                 <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                     <div className="flex items-center gap-2 mb-4 text-purple-600 font-bold">
                         <Palette className="w-5 h-5" />
                         <h3>Cover Art</h3>
                     </div>
                     <div className="aspect-[3/4] bg-slate-100 rounded-xl mb-4 overflow-hidden relative group">
                         {project.cover_url ? (
                             <img src={`${process.env.REACT_APP_BACKEND_URL}${project.cover_url}`} className="w-full h-full object-cover" alt="Cover" />
                         ) : (
                             <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 p-8 text-center">
                                 <ImageIcon className="w-12 h-12 mb-2" />
                                 <p className="text-sm">No cover generated yet</p>
                             </div>
                         )}
                     </div>
                     <button
                        onClick={handleGenerateCover}
                        disabled={generatingCover}
                        className="w-full py-2 bg-purple-100 text-purple-700 hover:bg-purple-200 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                     >
                        {generatingCover ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Palette className="w-4 h-4"/>}
                        Generate AI Cover
                     </button>
                 </div>
            </div>
        </div>
      </div>
    );
  }

  if (generating) {
      return (
          <div className="flex flex-col items-center justify-center py-20">
              <Sparkles className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
              <h3 className="text-xl font-bold text-slate-900">The AI Art Director is working...</h3>
              <p className="text-slate-500">Choosing fonts, mixing colors, and arranging your memories.</p>
          </div>
      )
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex h-[calc(100vh-200px)]">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-slate-200 p-4 overflow-y-auto flex-shrink-0">
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">
            Available Photos
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {availablePhotos.map((photo) => (
              <DraggablePhoto key={photo.id} photo={photo} />
            ))}
          </div>
        </div>

        {/* Main: Book Pages with Dynamic Design */}
        <div className="flex-1 bg-slate-100 p-8 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="flex justify-between items-center mb-6">
                 <h2 className="text-2xl font-bold text-slate-800">Your Bespoke Storybook</h2>
                 <button 
                    onClick={() => setLayout(null)}
                    className="text-sm text-red-500 hover:underline"
                 >
                    Reset Layout
                 </button>
            </div>
            
            {layout.map((page, pageIndex) => {
                // Apply Dynamic Design
                const design = page.design || {};
                const pageStyle = {
                    backgroundColor: design.background_color || '#ffffff',
                    color: design.text_color || '#1e293b',
                    fontFamily: fontMap[design.font_family] || fontMap['Modern']
                };

                return (
                  <div key={pageIndex} className="rounded-xl shadow-lg overflow-hidden transition-all duration-500" style={pageStyle}>
                    <div className="p-8">
                      <div className="flex justify-between items-baseline mb-2">
                        <span 
                            className="text-xs font-bold uppercase tracking-wide opacity-70"
                            style={{ color: design.accent_color }}
                        >
                          Page {page.page_number} • {design.layout_style} style
                        </span>
                      </div>
                      <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: fontMap[design.font_family] }}>
                        {page.title}
                      </h3>
                      <p className="leading-relaxed text-lg opacity-90">
                        {page.narrative}
                      </p>
                    </div>
    
                    <div className="p-8 pt-0">
                      <div className={`grid gap-6 ${
                          page.photo_slots === 1 ? "grid-cols-1 max-w-md mx-auto" : 
                          page.photo_slots === 2 ? "grid-cols-2" : 
                          "grid-cols-2 md:grid-cols-3"
                      }`}>
                        {Array.from({ length: page.photo_slots }).map((_, slotIndex) => {
                            const assignedId = page.assigned_photo_ids ? page.assigned_photo_ids[slotIndex] : null;
                            const assignedPhoto = assignedId ? getPhotoById(assignedId) : null;
                            
                            return (
                                <PhotoSlot 
                                    key={slotIndex} 
                                    assignedPhoto={assignedPhoto} 
                                    onDrop={(item) => handleDropPhoto(pageIndex, slotIndex, item)}
                                    onRemove={() => handleRemovePhoto(pageIndex, slotIndex)}
                                    design={design}
                                />
                            )
                        })}
                      </div>
                    </div>
                  </div>
                );
            })}
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

export default MagicDesigner;
